import {
  Color,
  DataTexture,
  FloatType,
  Matrix4,
  MeshStandardMaterial,
  NearestFilter,
  RGBAFormat,
  Vector3,
  Vector4,
  type Material,
  type Object3D,
  type WebGLProgramParametersWithUniforms
} from "three";

/**
 * Подсветка мебели без «наклеек».
 *
 * LED-лента в профиле — это светящийся отрезок. Настоящие источники three.js
 * здесь не годятся: точечный без теней светит сквозь полки, у прямоугольного
 * (RectAreaLight) теней нет вовсе, а тень на каждую ячейку не потянет ни одна
 * видеокарта. Поэтому свет считается прямо в шейдере материалов:
 *
 * - освещённость от отрезка берётся точной формулой (интеграл по длине ленты),
 *   поэтому у ленты нет «бусин» и пятен, а яркость спадает как у настоящей;
 * - профиль светит в свою сторону (вниз, в ячейку, на стену) — по косинусу,
 *   как рассеиватель;
 * - свет заперт в объёме, который он реально освещает: в отсеке ячейки,
 *   на полу перед цоколем, на стене за стеллажом. Полки и стойки этот объём
 *   ограничивают, поэтому свет не проходит сквозь них;
 * - внутри отсека добавляется ровный переотражённый свет: светлый отсек
 *   светится весь, а в чёрном остаются глубокие тени.
 */

export interface CellLightLine {
  /** Концы ленты и направление, куда смотрит рассеиватель, — в координатах владельца. */
  a: Vector3;
  b: Vector3;
  direction: Vector3;
  /** Цвет, умноженный на силу света с метра ленты. */
  color: Color;
  /** Показатель косинуса: 1 — рассеиватель, больше — узкий луч светильника. */
  lobe: number;
  /** Полутолщина светящейся полосы, м: сглаживает свет у самой ленты. */
  radius?: number;
}

export interface CellLightVolume {
  /** Объект, в координатах которого заданы объём и ленты (группа модуля). */
  owner: Object3D;
  min: Vector3;
  max: Vector3;
  lights: CellLightLine[];
  /** Переотражённый свет внутри объёма (освещённость, цвет × сила). */
  bounce: Color;
  /**
   * Мягкость только внешних границ объёма по осям владельца, м. Нулевой
   * компонент оставляет жёсткую физическую границу (полку/стойку/задник).
   */
  edgeFade?: Vector3;
  /**
   * «Колено» экспозиции этого объёма: освещённость, при которой свет сжимается
   * вдвое. В отсеке оно низкое (белый отсек не становится световым коробом),
   * у света на стену и пол — высокое: у кромки стена почти белая, как на фото.
   */
  knee?: number;
  /** Предметы на полке — сферы (центр и радиус в координатах владельца): дают мягкую тень. */
  occluders?: Vector4[];
}

const TEXELS_PER_ROW = 7;
/** Потолок циклов шейдера; реальное число берётся из текстуры. */
const MAX_BOXES = 128;
const MAX_LIGHTS_PER_BOX = 8;
const MAX_OCCLUDERS_PER_BOX = 8;
/** Сколько точек ленты проверяется на тень от предметов. */
const SHADOW_SAMPLES = 4;
/** Запас объёма: грань полки лежит ровно на границе отсека. */
const BOX_MARGIN = 0.0015;
const DEFAULT_RADIUS = 0.008;
/** «Колено» экспозиции по умолчанию: освещённость, при которой свет ленты сжимается вдвое. */
const LIGHT_KNEE = "3.0";

const PARS = /* glsl */ `
uniform highp sampler2D egelCellLights;
uniform int egelCellBoxCount;
uniform vec3 egelCellBoundsMin;
uniform vec3 egelCellBoundsMax;

// Освещённость точки p (нормаль n) от светящегося отрезка a-b с силой 1 на метр.
// radius2 — квадрат полутолщины полосы: сглаживает свет у самой ленты.
//
// Рассеиватель ленты (lobe = 1) — ламбертов: сила света ∝ cos угла к его
// оси e, а e перпендикулярна ленте. Тогда h = e·(p - a) одинаково для всех
// точек ленты и освещённость — точный интеграл h·(n·r)/|r|^4 по длине:
// у торца ленты боковина не пересвечена, свет спадает как у настоящей.
// Узкий луч светильника (lobe > 1, короткий отрезок) — как точечный источник.
float egelLineIrradiance( vec3 p, vec3 n, vec3 a, vec3 b, vec3 e, float radius2, float lobe ) {
  vec3 d = b - a;
  vec3 r0 = a - p;
  float qa = max( dot( d, d ), 1e-8 );
  float qb = dot( r0, d );
  float qc = dot( r0, r0 ) + radius2;
  float delta = max( qa * qc - qb * qb, 1e-12 );
  float nr0 = dot( n, r0 );
  float nd = dot( n, d );
  float result = 0.0;
  if ( lobe < 1.5 ) {
    float h = - dot( e, r0 );
    if ( h > 0.0 ) {
      float root = sqrt( delta );
      float q1 = qa + 2.0 * qb + qc;
      float i1 = atan( ( qa + qb ) / root ) / root;
      float i0 = atan( qb / root ) / root;
      float j0 = ( ( qa + qb ) / q1 - qb / qc + qa * ( i1 - i0 ) ) / ( 2.0 * delta );
      float j1 = ( 1.0 - ( qb + qc ) / q1 - qb * ( i1 - i0 ) ) / ( 2.0 * delta );
      result = max( h * ( nr0 * j0 + nd * j1 ), 0.0 ) * sqrt( qa );
    }
  } else {
    float f1 = ( nr0 * ( qa + qb ) - nd * ( qb + qc ) ) * inversesqrt( max( qa + 2.0 * qb + qc, 1e-12 ) );
    float f0 = ( nr0 * qb - nd * qc ) * inversesqrt( qc );
    float irradiance = max( ( f1 - f0 ) / delta, 0.0 ) * sqrt( qa );
    float t = clamp( - qb / qa, 0.0, 1.0 );
    vec3 toP = p - ( a + d * t );
    float cosE = dot( e, toP ) * inversesqrt( dot( toP, toP ) + radius2 );
    result = irradiance * pow( max( cosE, 0.0 ), lobe );
  }
  return result;
}

// Видимая доля светящегося диска (точка q, угловой радиус по lightRadius)
// из точки p, когда между ними сфера-предмет: мягкая тень с полутенью.
float egelSphereVisibility( vec3 p, vec3 q, vec4 sphere, float lightRadius ) {
  vec3 s = sphere.xyz - p;
  float sl = length( s );
  if ( sl < sphere.w * 1.15 ) return 1.0;
  vec3 d = q - p;
  float dist = length( d );
  float proj = dot( s, d ) / dist;
  if ( proj <= 0.0 || proj >= dist ) return 1.0;
  float occluder = asin( min( sphere.w / sl, 1.0 ) );
  float source = max( atan( lightRadius / dist ), 0.035 );
  float phi = acos( clamp( proj / sl, - 1.0, 1.0 ) );
  float darkest = 1.0 - min( 1.0, ( occluder * occluder ) / ( source * source ) );
  return mix( darkest, 1.0, smoothstep( abs( occluder - source ), occluder + source, phi ) );
}

// Доля света ленты a-b, дошедшая до p мимо предметов отсека.
float egelLineVisibility( vec3 p, vec3 n, vec3 a, vec3 b, vec3 e, float lobe, int firstOccluder, int occluders ) {
  float lit = 0.0;
  float total = 0.0;
  float span = length( b - a );
  int samples = span < 0.06 ? 1 : ${SHADOW_SAMPLES};
  float lightRadius = span / float( 2 * samples ) + 0.01;
  for ( int i = 0; i < ${SHADOW_SAMPLES}; i ++ ) {
    if ( i >= samples ) break;
    vec3 q = mix( a, b, ( float( i ) + 0.5 ) / float( samples ) );
    vec3 l = q - p;
    float d2 = max( dot( l, l ), 1e-6 );
    l *= inversesqrt( d2 );
    float weight = max( dot( n, l ), 0.0 ) * pow( max( dot( e, - l ), 0.0 ), lobe ) / d2;
    float visible = 1.0;
    for ( int j = 0; j < ${MAX_OCCLUDERS_PER_BOX}; j ++ ) {
      if ( j >= occluders ) break;
      visible *= egelSphereVisibility( p, q, texelFetch( egelCellLights, ivec2( 0, firstOccluder + j ), 0 ), lightRadius );
    }
    lit += weight * visible;
    total += weight;
  }
  return total > 0.0 ? lit / total : 1.0;
}

float egelVolumeEdge( float distanceToEdge, float fade ) {
  return fade > 0.00001 ? smoothstep( 0.0, fade, max( distanceToEdge, 0.0 ) ) : 1.0;
}
`;

const APPLY = /* glsl */ `
if ( egelCellBoxCount > 0 ) {
  vec3 egelP = ( - vViewPosition - viewMatrix[ 3 ].xyz ) * mat3( viewMatrix );
  if ( all( greaterThanEqual( egelP, egelCellBoundsMin ) ) && all( lessThanEqual( egelP, egelCellBoundsMax ) ) ) {
    vec3 egelN = inverseTransformDirection( normal, viewMatrix );
    vec3 egelV = normalize( cameraPosition - egelP );
    vec3 egelR = reflect( - egelV, egelN );
    float egelAlpha = pow2( material.roughness );
    vec3 egelDirect = vec3( 0.0 );
    vec3 egelSpecular = vec3( 0.0 );
    vec3 egelBounce = vec3( 0.0 );
    for ( int box = 0; box < ${MAX_BOXES}; box ++ ) {
      if ( box >= egelCellBoxCount ) break;
      vec4 m0 = texelFetch( egelCellLights, ivec2( 0, box ), 0 );
      vec4 m1 = texelFetch( egelCellLights, ivec2( 1, box ), 0 );
      vec4 m2 = texelFetch( egelCellLights, ivec2( 2, box ), 0 );
      vec3 local = vec3( dot( m0.xyz, egelP ) + m0.w, dot( m1.xyz, egelP ) + m1.w, dot( m2.xyz, egelP ) + m2.w );
      vec4 lo = texelFetch( egelCellLights, ivec2( 3, box ), 0 );
      vec4 hi = texelFetch( egelCellLights, ivec2( 4, box ), 0 );
      if ( any( lessThan( local, lo.xyz ) ) || any( greaterThan( local, hi.xyz ) ) ) continue;
      vec4 bounce = texelFetch( egelCellLights, ivec2( 5, box ), 0 );
      vec4 fade = texelFetch( egelCellLights, ivec2( 6, box ), 0 );
      vec3 edgeDistance = min( local - lo.xyz, hi.xyz - local );
      float volumeWeight = egelVolumeEdge( edgeDistance.x, fade.x )
        * egelVolumeEdge( edgeDistance.y, fade.y )
        * egelVolumeEdge( edgeDistance.z, fade.z );
      vec3 boxDirect = vec3( 0.0 );
      vec3 boxSpecular = vec3( 0.0 );
      vec3 boxBounce = bounce.rgb * volumeWeight;
      int first = int( lo.w + 0.5 );
      int count = int( hi.w + 0.5 );
      int occluders = int( bounce.w + 0.5 );
      for ( int index = 0; index < ${MAX_LIGHTS_PER_BOX}; index ++ ) {
        if ( index >= count ) break;
        int row = first + index;
        vec4 la = texelFetch( egelCellLights, ivec2( 0, row ), 0 );
        vec4 lb = texelFetch( egelCellLights, ivec2( 1, row ), 0 );
        vec4 le = texelFetch( egelCellLights, ivec2( 2, row ), 0 );
        vec4 lc = texelFetch( egelCellLights, ivec2( 3, row ), 0 );
        float egelE = egelLineIrradiance( egelP, egelN, la.xyz, lb.xyz, le.xyz, la.w, lb.w );
        egelE *= volumeWeight;
        if ( egelE <= 0.0 ) continue;
        if ( occluders > 0 ) egelE *= egelLineVisibility( egelP, egelN, la.xyz, lb.xyz, le.xyz, lb.w, first + count, occluders );
        boxDirect += lc.rgb * egelE;
        // Блик: точка ленты, ближайшая к отражённому лучу; лепесток шире
        // на угловой размер ленты, чтобы энергия не росла (Karis 2013).
        vec3 L0 = la.xyz - egelP;
        vec3 Ld = lb.xyz - la.xyz;
        float rd = dot( egelR, Ld );
        float t = clamp( ( dot( egelR, L0 ) * rd - dot( L0, Ld ) ) / max( dot( Ld, Ld ) - rd * rd, 1e-8 ), 0.0, 1.0 );
        vec3 egelL = normalize( L0 + Ld * t );
        // Нормаль почти вдоль взгляда или ленты (тонкая ткань с двух сторон,
        // сглаженные нормали): у GGX там деление почти на ноль — без этой
        // заглушки одежда под лентой покрывается пересвеченными пятнами.
        float egelNL = dot( egelN, egelL );
        float egelNV = dot( egelN, egelV );
        if ( egelNL <= 0.0 || egelNV <= 0.0 ) continue;
        float grazing = clamp( egelNL * 10.0, 0.0, 1.0 ) * clamp( egelNV * 10.0, 0.0, 1.0 );
        float extent = acos( clamp( dot( normalize( L0 ), normalize( L0 + Ld ) ), - 1.0, 1.0 ) );
        float widen = egelAlpha / min( 1.0, egelAlpha + 0.25 * extent + 0.5 * sqrt( la.w ) / max( length( L0 + Ld * t ), 1e-3 ) );
        boxSpecular += lc.rgb * egelE * widen * grazing * BRDF_GGX( egelL, egelV, egelN, material );
      }
      // Мягкое «колено», как экспозиция фотокамеры: у самой ленты свет остаётся
      // горячим, но белый отсек не превращается в ровный световой короб.
      // Блик тоже: иначе глянцевый предмет у ленты ярче рассеивателя и
      // в постобработке загорается, как факел.
      float knee = fade.w > 0.0 ? fade.w : ${LIGHT_KNEE};
      float boxLum = dot( boxDirect + boxBounce, vec3( 0.2126, 0.7152, 0.0722 ) );
      float boxKnee = 1.0 / ( 1.0 + boxLum / knee );
      egelDirect += boxDirect * boxKnee;
      egelBounce += boxBounce * boxKnee;
      egelSpecular += boxSpecular / ( 1.0 + dot( boxSpecular, vec3( 0.2126, 0.7152, 0.0722 ) ) / knee );
    }
    reflectedLight.directDiffuse += egelDirect * BRDF_Lambert( material.diffuseColor );
    reflectedLight.directSpecular += egelSpecular;
    reflectedLight.indirectDiffuse += egelBounce * BRDF_Lambert( material.diffuseColor );
  }
}
`;

type Vec = { x: number; y: number; z: number };
const dot = (u: Vec, v: Vec) => u.x * v.x + u.y * v.y + u.z * v.z;
const sub = (u: Vec, v: Vec) => ({ x: u.x - v.x, y: u.y - v.y, z: u.z - v.z });

/**
 * То же, что egelLineIrradiance для рассеивателя (lobe = 1), на TypeScript:
 * тест сверяет формулу с численным интегралом.
 */
export function diffuserLineIrradiance(p: Vec, n: Vec, a: Vec, b: Vec, e: Vec, radius2 = 0) {
  const d = sub(b, a);
  const r0 = sub(a, p);
  const qa = Math.max(dot(d, d), 1e-8);
  const qb = dot(r0, d);
  const qc = dot(r0, r0) + radius2;
  const delta = Math.max(qa * qc - qb * qb, 1e-12);
  const h = -dot(e, r0);
  if (h <= 0) return 0;
  const root = Math.sqrt(delta);
  const q1 = qa + 2 * qb + qc;
  const i1 = Math.atan((qa + qb) / root) / root;
  const i0 = Math.atan(qb / root) / root;
  const j0 = ((qa + qb) / q1 - qb / qc + qa * (i1 - i0)) / (2 * delta);
  const j1 = (1 - (qb + qc) / q1 - qb * (i1 - i0)) / (2 * delta);
  return Math.max(h * (dot(n, r0) * j0 + dot(n, d) * j1), 0) * Math.sqrt(qa);
}

/** Вставка в шейдер MeshStandard/MeshPhysical; экспорт — для теста. */
export function injectCellLights(fragmentShader: string) {
  if (!fragmentShader.includes("#include <lights_fragment_end>") || !fragmentShader.includes("void main() {")) {
    return fragmentShader;
  }
  return fragmentShader
    .replace("void main() {", `${PARS}\nvoid main() {`)
    .replace("#include <lights_fragment_end>", `#include <lights_fragment_end>\n${APPLY}`);
}

/**
 * Упаковка объёмов и лент в строки текстуры (по 7 текселей):
 * объём — строки мира→владелец, min + первая лента, max + число лент,
 * переотражение + число предметов, плавность внешних границ + «колено»; лента — A + радиус², B + показатель,
 * направление, цвет; предмет — центр + радиус (строки сразу за лентами).
 */
export function packCellLights(volumes: CellLightVolume[], worldMatrices: Matrix4[]) {
  const lightCount = volumes.reduce((sum, volume) => sum + Math.min(volume.lights.length, MAX_LIGHTS_PER_BOX)
    + Math.min(volume.occluders?.length ?? 0, MAX_OCCLUDERS_PER_BOX), 0);
  const boxCount = Math.min(volumes.length, MAX_BOXES);
  const rows = boxCount + lightCount;
  const data = new Float32Array(Math.max(1, rows) * TEXELS_PER_ROW * 4);
  const boundsMin = new Vector3(Infinity, Infinity, Infinity);
  const boundsMax = new Vector3(-Infinity, -Infinity, -Infinity);
  const inverse = new Matrix4();
  const corner = new Vector3();
  const point = new Vector3();
  let lightRow = boxCount;
  for (let box = 0; box < boxCount; box += 1) {
    const volume = volumes[box]!;
    const world = worldMatrices[box]!;
    inverse.copy(world).invert();
    const e = inverse.elements;
    const offset = box * TEXELS_PER_ROW * 4;
    // Матрица three.js хранится по столбцам: строка i — e[i], e[i+4], e[i+8], e[i+12].
    for (let row = 0; row < 3; row += 1) {
      data.set([e[row]!, e[row + 4]!, e[row + 8]!, e[row + 12]!], offset + row * 4);
    }
    const lights = volume.lights.slice(0, MAX_LIGHTS_PER_BOX);
    data.set([volume.min.x - BOX_MARGIN, volume.min.y - BOX_MARGIN, volume.min.z - BOX_MARGIN, lightRow], offset + 12);
    data.set([volume.max.x + BOX_MARGIN, volume.max.y + BOX_MARGIN, volume.max.z + BOX_MARGIN, lights.length], offset + 16);
    const occluders = (volume.occluders ?? []).slice(0, MAX_OCCLUDERS_PER_BOX);
    data.set([volume.bounce.r, volume.bounce.g, volume.bounce.b, occluders.length], offset + 20);
    data.set([volume.edgeFade?.x ?? 0, volume.edgeFade?.y ?? 0, volume.edgeFade?.z ?? 0, volume.knee ?? 0], offset + 24);
    for (let index = 0; index < 8; index += 1) {
      corner.set(
        index & 1 ? volume.max.x : volume.min.x,
        index & 2 ? volume.max.y : volume.min.y,
        index & 4 ? volume.max.z : volume.min.z
      ).applyMatrix4(world);
      boundsMin.min(corner);
      boundsMax.max(corner);
    }
    lights.forEach((light) => {
      const at = lightRow * TEXELS_PER_ROW * 4;
      const radius = light.radius ?? DEFAULT_RADIUS;
      point.copy(light.a).applyMatrix4(world);
      data.set([point.x, point.y, point.z, radius * radius], at);
      point.copy(light.b).applyMatrix4(world);
      data.set([point.x, point.y, point.z, light.lobe], at + 4);
      point.copy(light.direction).transformDirection(world);
      data.set([point.x, point.y, point.z, 0], at + 8);
      data.set([light.color.r, light.color.g, light.color.b, 0], at + 12);
      lightRow += 1;
    });
    // Сразу за лентами объёма — его предметы.
    occluders.forEach((sphere) => {
      point.set(sphere.x, sphere.y, sphere.z).applyMatrix4(world);
      data.set([point.x, point.y, point.z, sphere.w], lightRow * TEXELS_PER_ROW * 4);
      lightRow += 1;
    });
  }
  if (!boxCount) {
    boundsMin.set(1, 1, 1);
    boundsMax.set(-1, -1, -1);
  } else {
    boundsMin.subScalar(BOX_MARGIN * 2);
    boundsMax.addScalar(BOX_MARGIN * 2);
  }
  return { data, rows, boxCount, boundsMin, boundsMax };
}

export class CellLightSystem {
  private volumes: CellLightVolume[] = [];
  private texture: DataTexture;
  private capacity = 0;
  private previous = new Float32Array(0);
  private patched = new Set<Material>();
  private materialsDirty = true;
  /** Шейдер не собрался на этой видеокарте — подсветка выключена до перезагрузки. */
  private disabled = false;
  readonly uniforms = {
    egelCellLights: { value: null as unknown as DataTexture },
    egelCellBoxCount: { value: 0 },
    egelCellBoundsMin: { value: new Vector3(1, 1, 1) },
    egelCellBoundsMax: { value: new Vector3(-1, -1, -1) }
  };

  constructor() {
    this.texture = this.createTexture(16);
  }

  get count() {
    return this.volumes.length;
  }

  get all(): readonly CellLightVolume[] {
    return this.volumes;
  }

  clear() {
    this.volumes = [];
    this.materialsDirty = true;
  }

  add(volume: CellLightVolume) {
    if (!volume.lights.length) return;
    this.volumes.push(volume);
    this.materialsDirty = true;
    this.previous = new Float32Array(0);
  }

  /** Новые материалы (после перестройки сцены) получают подсветку при следующем кадре. */
  markMaterialsDirty() {
    this.materialsDirty = true;
  }

  /**
   * Перед кадром: подключить свет к новым материалам и обновить данные, если
   * что-то сдвинулось. Учитываются только объёмы, чьи владельцы стоят в сцене.
   */
  update(scene: Object3D, isInScene: (object: Object3D) => boolean) {
    if (this.disabled) return;
    const live = this.volumes.filter((volume) => isInScene(volume.owner));
    if (!live.length && this.uniforms.egelCellBoxCount.value === 0) return;
    if (live.length && this.materialsDirty) {
      this.materialsDirty = false;
      // Список подключённых материалов — только те, что сейчас в сцене.
      const inScene = new Set<Material>();
      scene.traverse((object) => {
        const material = (object as { material?: Material | Material[] }).material;
        if (!material) return;
        (Array.isArray(material) ? material : [material]).forEach((item) => {
          inScene.add(item);
          this.patch(item);
        });
      });
      this.patched = new Set([...this.patched].filter((material) => inScene.has(material)));
    }
    const matrices = live.map((volume) => {
      volume.owner.updateWorldMatrix(true, false);
      return volume.owner.matrixWorld;
    });
    const packed = packCellLights(live, matrices);
    this.uniforms.egelCellBoxCount.value = packed.boxCount;
    this.uniforms.egelCellBoundsMin.value.copy(packed.boundsMin);
    this.uniforms.egelCellBoundsMax.value.copy(packed.boundsMax);
    if (sameData(packed.data, this.previous)) return;
    this.previous = packed.data;
    if (packed.rows > this.capacity) {
      this.texture.dispose();
      let rows = 16;
      while (rows < packed.rows) rows *= 2;
      this.texture = this.createTexture(rows);
    }
    const target = this.texture.image.data as Float32Array;
    target.fill(0);
    target.set(packed.data);
    this.texture.needsUpdate = true;
  }

  /**
   * Запасной путь: если шейдер с подсветкой не собрался (старая видеокарта,
   * драйвер), материалы возвращаются к обычному освещению, сцена работает.
   */
  disable() {
    if (this.disabled) return;
    this.disabled = true;
    this.uniforms.egelCellBoxCount.value = 0;
    this.patched.forEach((material) => {
      const original = material.userData.egelCellLightsOriginal as
        | { compile: Material["onBeforeCompile"]; key: Material["customProgramCacheKey"] }
        | undefined;
      if (!original) return;
      material.onBeforeCompile = original.compile;
      material.customProgramCacheKey = original.key;
      delete material.userData.egelCellLightsOriginal;
      material.needsUpdate = true;
    });
    this.patched.clear();
  }

  get isDisabled() {
    return this.disabled;
  }

  dispose() {
    this.texture.dispose();
    this.volumes = [];
    this.patched.clear();
  }

  private createTexture(rows: number) {
    const texture = new DataTexture(
      new Float32Array(TEXELS_PER_ROW * rows * 4),
      TEXELS_PER_ROW,
      rows,
      RGBAFormat,
      FloatType
    );
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    this.capacity = rows;
    this.uniforms.egelCellLights.value = texture;
    return texture;
  }

  private patch(material: Material) {
    if (!(material instanceof MeshStandardMaterial)) return;
    this.patched.add(material);
    if (material.userData.egelCellLightsOriginal) return;
    const uniforms = this.uniforms;
    const previousCompile = material.onBeforeCompile;
    const previousKey = material.customProgramCacheKey;
    // Не сериализуется в JSON (функции), копией материала не переносится.
    Object.defineProperty(material.userData, "egelCellLightsOriginal", {
      value: { compile: previousCompile, key: previousKey },
      enumerable: false,
      configurable: true,
      writable: true
    });
    material.onBeforeCompile = function (this: Material, shader: WebGLProgramParametersWithUniforms, renderer) {
      previousCompile.call(this, shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.fragmentShader = injectCellLights(shader.fragmentShader);
    };
    material.customProgramCacheKey = function (this: Material) {
      return `${previousKey.call(this)}|egel-cell-lights-4`;
    };
    material.needsUpdate = true;
  }
}

function sameData(a: Float32Array, b: Float32Array) {
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
}
